import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Link from "../../components/Link";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  IconButton
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import MuiCircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import { AdminContainer } from "../../components/layouts";
import api from "../../api";
import dayjs from "dayjs";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "running", label: "Running" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "waiting", label: "Waiting" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" }
];

const STATUS_COLORS = {
  success: { bg: "#e8f5e9", color: "#2e7d32" },
  failed: { bg: "#ffebee", color: "#c62828" },
  running: { bg: "#fff3e0", color: "#e65100" },
  waiting: { bg: "#e3f2fd", color: "#1565c0" },
  paused: { bg: "#f3e5f5", color: "#6a1b9a" },
  cancelled: { bg: "#efebe9", color: "#4e342e" }
};

const TRIGGER_TYPE_LABELS = {
  "trigger.lead.created": "New lead added",
  "trigger.lead.updated": "Lead stage changed",
  "trigger.application.updated": "Application status change",
  "trigger.schedule": "Scheduled workflow"
};

const useStyles = makeStyles((theme) => ({
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center"
  },
  filterControl: {
    minWidth: 180
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  noWrap: {
    whiteSpace: "nowrap"
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return "—";
  const ms = dayjs(finishedAt).diff(dayjs(startedAt));
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

export default function WorkflowExecutionLogPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      partner_id: params.get("partner_id") || "",
      status: params.get("status") || ""
    };
  });

  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(() => {
    const pid = new URLSearchParams(location.search).get("partner_id");
    return pid ? { label: `Partner ${pid}`, value: parseInt(pid) } : null;
  });

  const [executions, setExecutions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPartnerLoading(true);
    api
      .getPartners({
        page: 1,
        search: partnerSearch,
        status: "active",
        perPage: 10
      })
      .then((resp) => {
        setPartnerOptions(
          resp.data.items.map((item) => ({
            label: item.name,
            value: item.id
          }))
        );
        setPartnerLoading(false);
      })
      .catch(() => {
        setPartnerLoading(false);
      });
  }, [partnerSearch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = {
      limit: rowsPerPage,
      offset: page * rowsPerPage
    };
    if (filters.partner_id) params.partner_id = filters.partner_id;
    if (filters.status) params.status = filters.status;

    try {
      const resp = await api.getWorkflowExecutionLog(params).then(api.pickData);
      setExecutions(resp?.executions || []);
      setTotal(resp?.total || 0);
    } catch (err) {
      console.error("Failed to fetch execution log:", err);
      setExecutions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.partner_id) params.set("partner_id", filters.partner_id);
    if (filters.status) params.set("status", filters.status);
    const search = params.toString();
    if (search !== location.search.replace("?", "")) {
      history.replace({ search: search ? `?${search}` : "" });
    }
  }, [filters, history, location.search]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
    setPage(0);
  };

  return (
    <AdminContainer>
      <Box className={classes.headerRow}>
        <Typography variant="h5" color="primary">
          Workflow Execution Log
        </Typography>
        <IconButton onClick={fetchData} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters */}
      <Box className={classes.filters}>
        <Autocomplete
          options={
            partnerLoading
              ? [{ label: "Loading...", value: null }]
              : partnerOptions
          }
          value={selectedPartner}
          onChange={(e, value) => {
            setSelectedPartner(value);
            setFilters((prev) => ({
              ...prev,
              partner_id: value ? value.value : ""
            }));
            setPage(0);
          }}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value.value
          }
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Partner"
              size="small"
              placeholder="All partners"
              InputProps={{
                ...params.InputProps,
                onChange: (e) => {
                  setPartnerSearch(e.target.value);
                },
                endAdornment: (
                  <React.Fragment>
                    {partnerLoading ? (
                      <MuiCircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterControl}
        >
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status}
            onChange={handleFilterChange("status")}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading && executions.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Exec #</TableCell>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Trigger Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No executions found
                    </TableCell>
                  </TableRow>
                ) : (
                  executions.map((row) => {
                    const statusStyle = STATUS_COLORS[row.status] || {};
                    return (
                      <TableRow
                        key={row.id}
                        className={classes.clickableRow}
                        onClick={() => history.push(
                          `/workflow-execution-log/${row.id}?workflow_id=${row.workflowId || row.workflow_id}`
                        )}
                      >
                        <TableCell className={classes.noWrap}>
                          #{row.executionNumber || row.execution_number}
                        </TableCell>
                        <TableCell>
                          {row.workflowName || row.workflow_name || `Workflow ${row.workflowId || row.workflow_id}`}
                        </TableCell>
                        <TableCell>
                          <Link to={`/partners/${row.partnerId || row.partner_id}`}>
                            {row.partnerName || row.partner_name || row.partnerId || row.partner_id}
                          </Link>
                        </TableCell>
                        <TableCell className={classes.noWrap}>
                          {TRIGGER_TYPE_LABELS[row.triggerNodeKind || row.trigger_node_kind] ||
                            row.triggerNodeKind || row.trigger_node_kind || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            style={{
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.color,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell className={classes.noWrap}>
                          {row.subjectType || row.subject_type
                            ? `${row.subjectType || row.subject_type} #${row.subjectId || row.subject_id}`
                            : "—"}
                        </TableCell>
                        <TableCell className={classes.noWrap}>
                          {row.startedAt || row.started_at
                            ? dayjs(row.startedAt || row.started_at).format("MMM D, YYYY HH:mm:ss")
                            : "—"}
                        </TableCell>
                        <TableCell className={classes.noWrap}>
                          {formatDuration(
                            row.startedAt || row.started_at,
                            row.finishedAt || row.finished_at
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      )}
    </AdminContainer>
  );
}
